'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslations } from 'next-intl';
import { useIntersection, revealClasses } from '@/lib/hooks/useIntersection';
import { fetchActiveFaqs } from '@/lib/store/slices/faqSlice';
import { useSocketEvent } from '@/lib/hooks/useSocket';
import { Pagination } from '@/components/ui/Pagination';
import { FilterBar } from '@/components/sections/FilterBar';
import type { RootState, AppDispatch } from '@/lib/store';
import type { FAQ } from '@/types';
import type { FilterValue } from '@/components/sections/FilterBar';

interface FAQClientProps { locale: string; initialFaqs?: FAQ[] }

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`w-5 h-5 transition-transform duration-300 shrink-0 ${open ? 'rotate-180' : ''}`}
      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function FAQItem({ faq, locale, isOpen, onToggle }: {
  faq: FAQ;
  locale: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const question = locale === 'ar' ? faq.question.ar : faq.question.en;
  const answer   = locale === 'ar' ? faq.answer.ar   : faq.answer.en;
  const bodyRef  = useRef<HTMLDivElement>(null);

  return (
    <div className="border border-(--color-border) rounded-xl overflow-hidden bg-(--color-card) transition-colors hover:border-primary-500">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-start font-medium text-(--color-text-primary) hover:text-primary-500 transition-colors"
        aria-expanded={isOpen}
      >
        <span className="text-sm sm:text-base leading-snug">{question}</span>
        <ChevronIcon open={isOpen} />
      </button>

      <div
        ref={bodyRef}
        style={{ maxHeight: isOpen ? bodyRef.current?.scrollHeight : 0, opacity: isOpen ? 1 : 0 }}
        className="overflow-hidden transition-all duration-300 ease-in-out"
      >
        <p className="px-5 pb-5 text-sm text-(--color-text-secondary) leading-relaxed">
          {answer}
        </p>
      </div>
    </div>
  );
}

function FAQSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="h-14 rounded-xl bg-(--color-card)" />
      ))}
    </div>
  );
}

export default function FAQClient({ locale, initialFaqs = [] }: FAQClientProps) {
  const t        = useTranslations('FAQ');
  const dispatch = useDispatch<AppDispatch>();
  const { items: faqs, loading, total, page, pages, limit } = useSelector((s: RootState) => s.faqs);

  // Show SSR data while Redux is loading OR when Redux returns empty
  const usingFallback = faqs.length === 0 && initialFaqs.length > 0;
  const displayFaqs   = usingFallback ? initialFaqs : faqs;
  const displayTotal  = usingFallback ? initialFaqs.length : total;
  const { ref, visible } = useIntersection<HTMLElement>();
  const isRtl = locale === 'ar';

  const [openId, setOpenId] = useState<string | null>(null);
  const [flash,  setFlash]  = useState(false);

  const sortOptions = [
    { value: 'order',         label: t('sortOrder') },
    { value: '-order',        label: t('sortOrderRev') },
    { value: 'question.en',   label: t('sortQuestionAZ') },
    { value: '-question.en',  label: t('sortQuestionZA') },
  ];

  const [filters, setFilters] = useState<FilterValue>({
    page: 1, limit: 6, sort: 'order',
  });

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const doFetch = useCallback((f: FilterValue) => {
    dispatch(fetchActiveFaqs(f));
  }, [dispatch]);

  useEffect(() => {
    doFetch(filters);
  }, [doFetch, filters]);

  const handleFilterChange = useCallback((newFilters: FilterValue) => {
    setFilters(newFilters);
  }, []);

  // Real-time: refetch when FAQs change on the backend
  useSocketEvent<{ title?: { en: string }; type?: string }>('notification:new', (data) => {
    if (data?.type === 'content_update' && data?.title?.en?.toLowerCase().includes('faq')) {
      doFetch(filtersRef.current);
      setFlash(true);
      setTimeout(() => setFlash(false), 3000);
    }
  });

  useSocketEvent<{ action?: string }>('agent:action', (data) => {
    if (data?.action?.includes('faq')) {
      doFetch(filtersRef.current);
      setFlash(true);
      setTimeout(() => setFlash(false), 3000);
    }
  });

  return (
    <main ref={ref} dir={isRtl ? 'rtl' : 'ltr'} className="min-h-screen py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className={`text-center mb-12 ${revealClasses(visible)}`}>
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-primary-500 mb-3">
            {t('eyebrow')}
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold text-(--color-text-primary) mb-4">
            {t('heading')}
          </h1>
          <p className="text-(--color-text-secondary) max-w-xl mx-auto">{t('desc')}</p>
        </div>

        {/* Live-update flash */}
        {flash && (
          <div className="mb-6 px-4 py-2 rounded-lg bg-primary-500/10 border border-primary-500/30 text-primary-500 text-sm text-center animate-pulse">
            ✦ {t('updated')}
          </div>
        )}

        {/* Search & controls */}
        <FilterBar
          categories={[]}
          sortOptions={sortOptions}
          locale={locale}
          value={filters}
          onChange={handleFilterChange}
          searchPlaceholder={t('search')}
          total={displayTotal}
        />

        {/* List */}
        {loading && displayFaqs.length === 0 ? (
          <FAQSkeleton />
        ) : displayFaqs.length === 0 ? (
          <p className="text-center text-(--color-text-muted) py-16">{t('empty')}</p>
        ) : (
          <div className="space-y-3">
            {displayFaqs.map(faq => (
              <FAQItem
                key={faq._id}
                faq={faq}
                locale={locale}
                isOpen={openId === faq._id}
                onToggle={() => setOpenId(openId === faq._id ? null : faq._id)}
              />
            ))}
          </div>
        )}

        {!loading && displayFaqs.length > 0 && (
          <div className="mt-10">
            <Pagination
              page={page}
              total={total}
              limit={limit}
              pages={pages}
              onPageChange={p => setFilters(f => ({ ...f, page: p }))}
            />
          </div>
        )}
      </div>
    </main>
  );
}
